import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import { axios } from '../../../../services/axios';
import { useQuery } from 'hooks/useCustomQuery';
import {
    multiSelectDataMapper,
    query,
    toSentenceCase,
} from '../../../../utils/utils';

import FormBase from '@/components/forms/new/FormBase';
// import OperatorConnectNameField from './formFields/OperatorConnectNameField';
import ServiceProfileVisibleScope from './formFields/ServiceProfileVisibleScope';
import OperatorConnectOperatorsField from './formFields/OperatorConnectOperatorsField';
import OperatorConnectCallingProfilesField from './formFields/OperatorConnectCallingProfilesField';
import OperatorConnectRegionsField from './formFields/OperatorConnectRegionsField';
import OperatorConnectVoicePoliciesField from './formFields/OperatorConnectVoicePoliciesField';
import OperatorConnectDialPlansField from './formFields/OperatorConnectDialPlansField';
import OperatorConnectAuthPolicyField from './formFields/OperatorConnectAuthPolicyField';
import updateServiceProfiles from '../../../../API/ServiceProfiles/updateServiceProfiles';
import createServiceProfile from '../../../../API/ServiceProfiles/createServiceProfile';
import BreadcrumbTrail from 'components/BreadcrumbTrail';

// import { Select as _MultiSelect, Select as _Select } from 'chakra-react-select';
// import Select from '../../../../components/v4/Select';
import getServiceProfiles from '../../../../API/getServiceProfiles';
//
import Alert from '../../../../components/v4/Alert';
import AlertDescription from '../../../../components/v4/AlertDescription';
import AlertIcon from '../../../../components/v4/AlertIcon';
import LoadingIndicator from '../../../../components/v4/LoadingIndicator';
import { Field } from 'formik';
import { validationSchemaOC } from './schema';
import useFieldValidation from '../../../../hooks/useFieldValidation';

const OperatorConnect = ({
    mode,
    data,
    handleBackButton,
    level,
    visibleScopeOptions,
}) => {
    const {
        currentWholesaler: wholesalerID,
        currentPartner: partnerID,
        currentWholesalerData,
        companiesAndPartners,
    } = useSelector((state) => state.navigationLists);

    const { requiredScope } = useSelector((state) => state.login);

    const { darkMode } = useSelector((state) => state.settings);

    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const DEFAULT_OC_PAYLOAD = {
        name: 'Operator Connect Wholesaler Profile',
        authPolicy: 2,
        serviceType: 'OperatorConnect',
        callingProfiles: [],
        operatorProfiles: [],
        regions: [],
        voicePolicies: [],
        dialPlans: [],
        partnerID: level === 'partner' ? partnerID : undefined,
        wholesalerID: level === 'partner' ? undefined : wholesalerID,
        visibleScope: level === 'partner' ? 20 : 60,
    };

    useEffect(() => {
        if (
            mode !== 'EDIT' &&
            level !== 'partner' &&
            currentWholesalerData?.name
        ) {
            handleChangeState({
                name: `${currentWholesalerData?.name} - Operator Connect Wholesaler Profile`,
            });
        } else if (
            mode !== 'EDIT' &&
            level === 'partner' &&
            companiesAndPartners?.length > 0
        ) {
            const partnerName = companiesAndPartners.find(
                ({ id }) => id === partnerID,
            )?.name;
            handleChangeState({
                name: `${partnerName} - Operator Connect Partner Profile`,
            });
        }
    }, [
        mode,
        companiesAndPartners,
        currentWholesalerData?.name,
        level,
        partnerID,
        wholesalerID,
    ]);

    const [modalState, setModalState] = useState(data || DEFAULT_OC_PAYLOAD);

    const authPolicyDropdown = [
        { value: 0, label: 'Disabled' },
        { value: 1, label: 'Enabled' },
        { value: 2, label: 'Required' },
    ];

    const { validatefn } = useFieldValidation(modalState, validationSchemaOC);

    // function to set modal state with a given payload
    const handleChangeState = (payload) => {
        setModalState((prev) => ({
            ...prev,
            ...payload,
        }));
    };

    /*
     * A function to get the current scope and append it for API calls
     */
    const scope =
        level === 'partner'
            ? `Partner/${data?.partnerID || partnerID}`
            : `Wholesaler/${data?.wholesalerID || wholesalerID}`;

    const operatorProfileIds = (modalState.operatorProfiles || [])
        .map((op) => op?.value ?? op)
        .filter(Boolean);
    const callingProfileIds = (modalState.callingProfiles || [])
        .map((cp) => cp?.value ?? cp)
        .filter(Boolean);
    const regionIds = (modalState.regions || [])
        .map((r) => r?.value ?? r)
        .filter(Boolean);

    const { data: callingProfiles = [] } = useQuery({
        queryKey: ['ocCallingProfiles', scope, operatorProfileIds],
        queryFn: async () => {
            const res = await axios.get(
                `/ServiceProfiles/OperatorConnect/${scope}/CallingProfiles/Available?OperatorProfileIDs=${query(modalState.operatorProfiles)}`,
            );
            return res.data.map(multiSelectDataMapper);
        },
        enabled: operatorProfileIds.length > 0,
    });

    const { data: regionsEnum = [] } = useQuery({
        queryKey: ['ocRegions', scope, operatorProfileIds, callingProfileIds],
        queryFn: async () => {
            const res = await axios.get(
                `/ServiceProfiles/OperatorConnect/${scope}/Regions/Available?OperatorProfileIDs=${query(modalState.operatorProfiles)}&CallingProfileIDs=${query(modalState.callingProfiles)}`,
            );
            return res.data.map(multiSelectDataMapper);
        },
        enabled: operatorProfileIds.length > 0 && callingProfileIds.length > 0,
    });

    const { data: voicePoliciesEnum = [] } = useQuery({
        queryKey: [
            'ocVoicePolicies',
            scope,
            operatorProfileIds,
            callingProfileIds,
            regionIds,
        ],
        queryFn: async () => {
            const res = await axios.get(
                `/ServiceProfiles/OperatorConnect/${scope}/VoicePolicies/Available?OperatorProfileIDs=${query(modalState.operatorProfiles)}&CallingProfileIDs=${query(modalState.callingProfiles)}&RegionIDs=${query(modalState.regions)}`,
            );
            return res.data.map(multiSelectDataMapper);
        },
        enabled:
            operatorProfileIds.length > 0 &&
            callingProfileIds.length > 0 &&
            regionIds.length > 0,
    });

    const operatorsQuery = useQuery({
        queryKey: ['ocOperatorProfiles', scope],
        queryFn: async () => {
            const res = await axios.get(
                `/serviceprofiles/operatorconnect/${scope}/operatorprofiles/available`,
            );
            return res.data.map(multiSelectDataMapper);
        },
    });

    const dialPlansQuery = useQuery({
        queryKey: ['ocDialPlans', scope],
        queryFn: async () => {
            const res = await axios.get(
                `/serviceprofiles/operatorconnect/${scope}/dialplans/available`,
            );
            return res.data.map(multiSelectDataMapper);
        },
    });

    const operatorsEnum = operatorsQuery?.data || [];
    const dialPlansEnum = dialPlansQuery?.data || [];
    const operatorsLoading = operatorsQuery?.isLoading;
    const dialPlansLoading = dialPlansQuery?.isLoading;

    useQuery({
        queryKey: ['ocSPData', data?.id, mode],
        queryFn: () => getServiceProfiles({ level, id: data?.id }),
        enabled: mode === 'EDIT' && Boolean(data),
        onSuccess: (spResult) => {
            setModalState({
                ...spResult.data,
                callingProfiles: spResult.data.callingProfiles?.map(
                    multiSelectDataMapper,
                ),
                operatorProfiles: spResult.data.operatorProfiles?.map(
                    multiSelectDataMapper,
                ),
                regions: spResult.data.regions?.map(multiSelectDataMapper),
                dialPlans: spResult.data.dialPlans?.map(multiSelectDataMapper),
                voicePolicies: spResult.data.voicePolicies?.map(
                    multiSelectDataMapper,
                ),
            });
        },
    });

    if (operatorsLoading || dialPlansLoading) {
        return <LoadingIndicator />;
    }

    // Helper to map value(s) to option(s) for select fields
    const mapToOptions = (values, options) => {
        if (!values || !options) return [];
        if (Array.isArray(values)) {
            return (
                values
                    .map((v) => options.find((o) => o.value === (v.value ?? v)))
                    ?.filter(Boolean) || []
            );
        }
        return (
            options.find((o) => o.value === (values.value ?? values)) || null
        );
    };

    // Formik initial values (always use modalState, which is updated after async fetch)
    const initialValues = {
        name: modalState?.name || '',
        authPolicy: modalState?.authPolicy ?? 2,
        serviceType: 'OperatorConnect',
        callingProfiles: mapToOptions(
            modalState?.callingProfiles,
            callingProfiles,
        ),
        operatorProfiles: mapToOptions(
            modalState?.operatorProfiles,
            operatorsEnum,
        ),
        regions: mapToOptions(modalState?.regions, regionsEnum),
        voicePolicies: mapToOptions(
            modalState?.voicePolicies,
            voicePoliciesEnum,
        ),
        dialPlans: mapToOptions(modalState?.dialPlans, dialPlansEnum),
        partnerID:
            level === 'partner'
                ? (modalState?.partnerID ?? partnerID)
                : undefined,
        wholesalerID:
            level === 'partner'
                ? undefined
                : (modalState?.wholesalerID ?? wholesalerID),
        visibleScope:
            modalState?.visibleScope ?? (level === 'partner' ? 20 : 60),
    };

    // Formik submit handler
    const finalFormSubmit = async (values, actions, ...res) => {
        setSubmitted(true);
        setIsLoading(true);
        setError(null);
        console.log(res);

        try {
            if (!validatefn(values)) {
                setError(
                    'There are some validation errors in the form. Please check and try again.',
                );
                actions.setSubmitting(false);
                setIsLoading(false);
                return;
            }
            if (mode === 'EDIT') {
                await updateServiceProfiles({
                    type: 'OperatorConnect',
                    payload: modalState,
                    level,
                    scope: requiredScope,
                });
                handleBackButton();
            } else {
                await createServiceProfile({
                    type: 'OperatorConnect',
                    payload: modalState,
                    level,
                    scope: requiredScope,
                });
                handleBackButton();
            }
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                    'An error occurred. Please try again later.',
            );
        } finally {
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };
    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Configurations', path: '' },
    ];
    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`${toSentenceCase(mode)} ${modalState.name || data?.name}`}
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                ufBackBtn="builderBackBtn"
                ufSaveBtn="builderSaveBtn"
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                enableReinitialize={true}>
                {({ errors = {}, touched = {} }) =>
                    [
                        <ServiceProfileVisibleScope
                            key="operatorConnectServiceProfileVisibleScope"
                            name="visibleScope"
                            options={visibleScopeOptions}
                            darkMode={darkMode}
                            minWidth="420px"
                            maxWidth="520px"
                            width="100%"
                            errors={errors}
                            touched={touched}
                            onChange={(e) => {
                                setModalState((prev) => ({
                                    ...prev,
                                    visibleScope: e.value,
                                }));
                            }}
                        />,
                        <OperatorConnectOperatorsField
                            key="operators"
                            value={modalState.operatorProfiles}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    operatorProfiles: val,
                                }))
                            }
                            options={operatorsEnum}
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            error={
                                !modalState.operatorProfiles ||
                                modalState.operatorProfiles.length === 0
                                    ? 'At least one operator is required'
                                    : ''
                            }
                            showError={submitted}
                            disableStackHeight={true}
                        />,
                        <OperatorConnectCallingProfilesField
                            key="callingProfiles"
                            value={modalState.callingProfiles}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    callingProfiles: val,
                                }))
                            }
                            options={callingProfiles}
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            error={
                                !modalState.callingProfiles ||
                                modalState.callingProfiles.length === 0
                                    ? 'At least one calling profile is required'
                                    : ''
                            }
                            showError={submitted}
                            disableStackHeight={true}
                        />,
                        <OperatorConnectRegionsField
                            key="regions"
                            value={modalState.regions}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    regions: val,
                                }))
                            }
                            options={regionsEnum}
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            error={
                                !modalState.regions ||
                                modalState.regions.length === 0
                                    ? 'At least one region is required'
                                    : ''
                            }
                            showError={submitted}
                            disableStackHeight={true}
                        />,
                        <OperatorConnectVoicePoliciesField
                            key="voicePolicies"
                            value={modalState.voicePolicies}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    voicePolicies: val,
                                }))
                            }
                            options={voicePoliciesEnum}
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            error={
                                !modalState.voicePolicies ||
                                modalState.voicePolicies.length === 0
                                    ? 'At least one voice policy is required'
                                    : ''
                            }
                            showError={submitted}
                            disableStackHeight={true}
                        />,
                        <OperatorConnectDialPlansField
                            key="dialPlans"
                            value={modalState.dialPlans}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    dialPlans: val,
                                }))
                            }
                            options={dialPlansEnum}
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            error={
                                !modalState.dialPlans ||
                                modalState.dialPlans.length === 0
                                    ? 'At least one dial plan is required'
                                    : ''
                            }
                            showError={submitted}
                            disableStackHeight={true}
                        />,
                        <Field
                            key="authPolicy"
                            name="authPolicy"
                            component={OperatorConnectAuthPolicyField}
                            options={[
                                { value: null, label: 'Select Auth Policy' },
                                ...authPolicyDropdown,
                            ]}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    authPolicy: val,
                                }))
                            }
                            darkMode={darkMode}
                            minWidth="420px"
                            maxWidth="520px"
                            width="100%"
                            errors={errors}
                            touched={touched}
                        />,
                        error && (
                            <Box
                                paddingBottom="0.5rem"
                                key="error-box-operator-connect">
                                <Alert
                                    key="error"
                                    status="error"
                                    mt={4}
                                    marginBottom="1rem">
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            </Box>
                        ),
                    ].filter(Boolean)
                }
            </FormBase>
        </>
    );
};

export default OperatorConnect;
